import { useCallback } from 'react';
import { useTranslation } from '../contexts/TranslationContext';

/**
 * Canonical stage names to dictionary keys.
 *
 * Keyed on the NAME rather than on `FunnelStage.semanticRole`, deliberately.
 * A role says what a stage means, not what it should be called, and the two
 * are not interchangeable here: `updateStageRoles` assigns `contacted` and
 * `qualified` to any stage in a template with no constraint on its name, and
 * `create-template.dto.ts` accepts `name` as free text. Keying the label off
 * the role would therefore rename a stage the customer called "Discovery Call"
 * to "Contacted" — replacing their data with our vocabulary. Meanwhile every
 * organisation created today gets `DEFAULT_STAGES`, whose names all resolve
 * here already, so role keying would take effect almost only where it is
 * wrong. `semanticRole` stays what PRs #876 and #879 use it for: behaviour.
 *
 * "closed won" / "closed lost" are the same two outcomes under the spelling
 * most CRMs use, and `won` / `lost` already resolve, so they resolve too.
 */
const STAGE_KEY_MAP: Record<string, string> = {
  prospect: 'leads.stages.prospect',
  contacted: 'leads.stages.contacted',
  qualified: 'leads.stages.qualified',
  proposal: 'leads.stages.proposal',
  won: 'leads.stages.won',
  'closed won': 'leads.stages.won',
  lost: 'leads.stages.lost',
  'closed lost': 'leads.stages.lost',
  negotiation: 'leads.stages.negotiation',
  customer: 'leads.stages.customer',
};

export function useTranslatedStageName() {
  const { t } = useTranslation();

  const translateStageName = useCallback(
    (name: string): string => {
      const key = STAGE_KEY_MAP[name.toLowerCase()];
      return key ? t(key) : name;
    },
    [t],
  );

  return translateStageName;
}
